import {
  MutationOptions,
  QueryOptions,
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';
import { UserService } from '@/service/user/user';
import { LifetimeGrant } from '@/service/user/user-types';
import { UserAdminService } from '@/service/user/user.admin';

export const useGetMeQuery = (
  opt?: QueryOptions<Awaited<ReturnType<typeof UserService.getMe>>>,
) =>
  useQuery({
    ...opt,
    queryFn: UserService.getMe,
    queryKey: ['UserService.getMe'],
  });

export const useUpdateUserMutation = (
  opt?: MutationOptions<
    Awaited<ReturnType<typeof UserService.updateUser>>,
    Error,
    Parameters<typeof UserService.updateUser>[0]
  >,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    ...opt,
    mutationFn: UserService.updateUser,
    onSuccess(data, variables, context) {
      queryClient.setQueryData(['UserService.getMe'], data);
      if (opt?.onSuccess) opt.onSuccess(data, variables, context);
    },
  });
};

export const useDeleteAccountMutation = (
  opt?: MutationOptions<
    Awaited<ReturnType<typeof UserService.deleteAccount>>,
    Error,
    Parameters<typeof UserService.deleteAccount>[0]
  >,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    ...opt,
    mutationFn: UserService.deleteAccount,
    onSuccess(data, variables, context) {
      // Le compte n'existe plus : tout ce qui est en cache le concerne.
      queryClient.clear();
      if (opt?.onSuccess) opt.onSuccess(data, variables, context);
    },
  });
};

export const useUploadAvatarMutation = (
  opt?: MutationOptions<
    Awaited<ReturnType<typeof UserService.uploadAvatar>>,
    Error,
    Parameters<typeof UserService.uploadAvatar>[0]
  >,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    ...opt,
    mutationFn: UserService.uploadAvatar,
    onSuccess(data, variables, context) {
      queryClient.setQueryData(['UserService.getMe'], data);
      if (opt?.onSuccess) opt.onSuccess(data, variables, context);
    },
  });
};

export const useDeleteAvatarMutation = (
  opt?: MutationOptions<
    Awaited<ReturnType<typeof UserService.deleteAvatar>>,
    Error
  >,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    ...opt,
    mutationFn: UserService.deleteAvatar,
    onSuccess(data, variables, context) {
      queryClient.setQueryData(['UserService.getMe'], data);
      if (opt?.onSuccess) opt.onSuccess(data, variables, context);
    },
  });
};

export const useUploadBannerMutation = (
  opt?: MutationOptions<
    Awaited<ReturnType<typeof UserService.uploadBanner>>,
    Error,
    Parameters<typeof UserService.uploadBanner>[0]
  >,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    ...opt,
    mutationFn: UserService.uploadBanner,
    onSuccess(data, variables, context) {
      queryClient.setQueryData(['UserService.getMe'], data);
      if (opt?.onSuccess) opt.onSuccess(data, variables, context);
    },
  });
};

export const useDeleteBannerMutation = (
  opt?: MutationOptions<
    Awaited<ReturnType<typeof UserService.deleteBanner>>,
    Error
  >,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    ...opt,
    mutationFn: UserService.deleteBanner,
    onSuccess(data, variables, context) {
      queryClient.setQueryData(['UserService.getMe'], data);
      if (opt?.onSuccess) opt.onSuccess(data, variables, context);
    },
  });
};

export const useGetUserSubscriptionQuery = (
  opt?: QueryOptions<Awaited<ReturnType<typeof UserService.getSubscription>>>,
) =>
  useQuery({
    ...opt,
    queryFn: UserService.getSubscription,
    queryKey: ['UserService.getSubscription'],
  });

export const useGetTrialStatusQuery = (
  opt?: QueryOptions<Awaited<ReturnType<typeof UserService.getTrialStatus>>>,
) =>
  useQuery({
    ...opt,
    queryFn: UserService.getTrialStatus,
    queryKey: ['UserService.getTrialStatus'],
  });

export const useActivateTrialMutation = (
  opt?: MutationOptions<
    Awaited<ReturnType<typeof UserService.activateTrial>>,
    Error
  >,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    ...opt,
    mutationFn: UserService.activateTrial,
    onSuccess(data, variables, context) {
      queryClient.setQueryData(['UserService.getTrialStatus'], data);
      queryClient.invalidateQueries({
        queryKey: ['UserService.getSubscription'],
      });
      if (opt?.onSuccess) opt.onSuccess(data, variables, context);
    },
  });
};

export const useUpdatePasswordMutation = (
  opt?: MutationOptions<
    Awaited<ReturnType<typeof UserService.updatePassword>>,
    Error,
    Parameters<typeof UserService.updatePassword>[0]
  >,
) => {
  return useMutation({
    ...opt,
    mutationFn: UserService.updatePassword,
    onSuccess(data, variables, context) {
      if (opt?.onSuccess) opt.onSuccess(data, variables, context);
    },
  });
};

export const useStartChangeEmailMutation = (
  opt?: MutationOptions<
    Awaited<ReturnType<typeof UserService.startChangeEmail>>,
    Error,
    Parameters<typeof UserService.startChangeEmail>[0]
  >,
) => {
  return useMutation({
    ...opt,
    mutationFn: UserService.startChangeEmail,
    onSuccess(data, variables, context) {
      if (opt?.onSuccess) opt.onSuccess(data, variables, context);
    },
  });
};

export const useConfirmChangeEmailMutation = (
  opt?: MutationOptions<
    Awaited<ReturnType<typeof UserService.confirmChangeEmail>>,
    Error,
    Parameters<typeof UserService.confirmChangeEmail>[0]
  >,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    ...opt,
    mutationFn: UserService.confirmChangeEmail,
    onSuccess(data, variables, context) {
      queryClient.invalidateQueries({ queryKey: ['UserService.getMe'] });
      if (opt?.onSuccess) opt.onSuccess(data, variables, context);
    },
  });
};

export const useMarkOnboardingCompleteMutation = (
  opt?: MutationOptions<
    Awaited<ReturnType<typeof UserService.completeOnboarding>>,
    Error
  >,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    ...opt,
    mutationFn: UserService.completeOnboarding,
    onSuccess(data, variables, context) {
      queryClient.setQueryData(['UserService.getMe'], data);
      if (opt?.onSuccess) opt.onSuccess(data, variables, context);
    },
  });
};

export const useSetHeardFromMutation = (
  opt?: MutationOptions<
    Awaited<ReturnType<typeof UserService.setHeardFrom>>,
    Error,
    Parameters<typeof UserService.setHeardFrom>[0]
  >,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    ...opt,
    mutationFn: UserService.setHeardFrom,
    onSuccess(data, variables, context) {
      queryClient.setQueryData(['UserService.getMe'], data);
      if (opt?.onSuccess) opt.onSuccess(data, variables, context);
    },
  });
};

/// Le profil remis à « jamais accueilli » ouvre aussitôt la question puis le
/// guide de bienvenue (voir `DashboardLayout`).
export const useReplayOnboardingMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: UserService.replayOnboarding,
    onSuccess(data) {
      queryClient.setQueryData(['UserService.getMe'], data);
    },
  });
};

export const useSetAdminRoleMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, admin }: { userId: string; admin: boolean }) =>
      admin
        ? UserAdminService.grantAdmin(userId)
        : UserAdminService.revokeAdmin(userId),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['UserAdminService.search'] }),
  });
};

export const useSearchUserQuery = (
  username: string,
  opt?: QueryOptions<Awaited<ReturnType<typeof UserService.search>>>,
) =>
  useQuery({
    ...opt,
    queryFn: () => UserService.search(username),
    queryKey: ['UserService.search', username],
    enabled: username.length > 0,
  });

export const useGetPublicUserQuery = (
  userId: string,
  opt?: QueryOptions<Awaited<ReturnType<typeof UserService.getPublicUser>>>,
) =>
  useQuery({
    ...opt,
    queryFn: () => UserService.getPublicUser(userId),
    queryKey: ['UserService.getPublicUser', userId],
  });

export const useGrantLifetimeMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, ...grant }: { userId: string } & LifetimeGrant) =>
      UserAdminService.grantLifetime(userId, grant),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['UserAdminService.search'] }),
  });
};

export const useRevokeLifetimeMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: UserAdminService.revokeLifetime,
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['UserAdminService.search'] }),
  });
};

export const useGrantAffiliationMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, alias }: { userId: string; alias?: string }) =>
      UserAdminService.grantAffiliation(userId, alias),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['UserAdminService.search'] }),
  });
};

export const useRevokeAffiliationMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: UserAdminService.revokeAffiliation,
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['UserAdminService.search'] }),
  });
};

export const useSearchUserAdminQuery = (
  params: Parameters<typeof UserAdminService.search>[0],
  opt?: Partial<
    UseQueryOptions<Awaited<ReturnType<typeof UserAdminService.search>>>
  >,
) =>
  useQuery({
    ...opt,
    queryFn: () => UserAdminService.search(params),
    // Les filtres font partie de la cle : sans eux, changer de page servait le
    // cache de la requete precedente, ce qui obligeait la page a rappeler
    // `refetch` a la main.
    queryKey: ['UserAdminService.search', params],
  });
